<template>
  <div class="step4-confirm">
    <!-- 확인 정보 모달 -->
    <div class="confirm-modal">
      <div class="modal-header">
        <h3>{{ $t("workorder_modal_confirm_title") }}</h3>
        <button class="close-btn" @click="prevStep">
          <img src="@/assets/img/step_4_close.svg" alt="" srcset="" />
        </button>
      </div>

      <div class="modal-body">
        <!-- 병원 정보 -->
        <div class="info-section">
          <div class="info-row">
            <label>{{ $t("workorder_label_hospital") }}</label>
            <span style="flex: none; margin-right: 10px">{{
              getHospitalName()
            }}</span>
            <span class="sub-text">{{ getHospitalPhone() }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_patient") }}</label>
            <span>{{ customerData?.name || "-" }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_phone") }}</label>
            <span>{{ formatPhone(customerData?.mobile) || "-" }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_chart_no") }}</label>
            <span>{{ customerData?.chartNumber || "-" }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_birthdate") }}</label>
            <span>{{ formatDate(customerData?.birth) || "-" }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_surgery_date") }}</label>
            <span>{{ formatDate(customerData?.surgeryDate) || "-" }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_work_date") }}</label>
            <span>{{ customerData?.worker?.workDate || "-" }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_delivery_date") }}</label>
            <span>{{
              customerData?.worker?.prostheticDeliveryDate || "-"
            }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_tooth_no") }}</label>
            <span v-html="getSelectedTeethText()"></span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_surgery_name") }}</label>
            <span>{{ $t("workorder_label_prosthetic") }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_manufacturer") }}</label>
            <span>{{ getManufacturerText() }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_detail_select") }}</label>
            <span>{{ getDetailSelectionText() }}</span>
          </div>
          <div class="info-row" v-if="getGtinCodeText()">
            <label>{{ $t("workorder_label_gtin") }}</label>
            <span>{{ getGtinCodeText() }}</span>
          </div>
          <div class="info-row" v-if="getManufacturingDateText()">
            <label>{{ $t("workorder_label_manufacture_date") }}</label>
            <span>{{ getManufacturingDateText() }}</span>
          </div>
          <div class="info-row" v-if="getLotNumberText()">
            <label>{{ $t("workorder_label_lot_no") }}</label>
            <span>{{ getLotNumberText() }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_product_name") }}</label>
            <span>{{ getProductText() }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_surface") }}</label>
            <span>{{ getSurfaceProcessText() }}</span>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="cancel-btn" @click="prevStep">
          {{ $t("workorder_btn_cancel") }}
        </button>
        <button class="confirm-btn" @click="complete">
          {{ $t("workorder_btn_confirm") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Step4",
  props: {
    clientData: {
      type: Object,
      default: () => ({}),
    },
    customerData: {
      type: Object,
      default: () => ({}),
    },
    prostheticData: {
      type: Object,
      default: () => ({}),
    },
    prevStep: {
      type: Function,
      default: () => {},
    },
    complete: {
      type: Function,
      default: () => {},
    },
  },
  methods: {
    formatPhone(value) {
      if (!value) return "";
      return value;
    },

    formatDate(value) {
      if (!value) return "";
      // YYYY-MM-DD 또는 YYYY/MM/DD 형식으로 반환
      if (typeof value === "string") {
        return value;
      }
      // Date 객체인 경우
      if (value instanceof Date) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, "0");
        const day = String(value.getDate()).padStart(2, "0");
        return `${year}/${month}/${day}`;
      }
      return value;
    },

    getSelectedTeethText() {
      if (
        this.prostheticData?.toothDetails &&
        this.prostheticData.toothDetails.length > 0
      ) {
        // 카드별로 치아번호 표시 (개행 포함)
        const lines = this.prostheticData.toothDetails.map((detail) => {
          const numbers = [...(detail.toothNumbers || [])]
            .sort((a, b) => a - b)
            .join(", ");
          const treatmentLabel = this.getTreatmentTypeLabel(
            detail.treatmentType
          );
          return `${numbers}(${treatmentLabel})`;
        });
        return lines.join("<br>");
      }
      return this.$t("workorder_select_tooth");
    },

    getTreatmentTypeLabel(treatmentType) {
      const typeMap = {
        crown: "workorder_surgery_crown",
        implant_crown: "workorder_surgery_implant_crown",
        bridge: "workorder_surgery_bridge",
        laminate: "workorder_surgery_laminate",
        inlay: "workorder_surgery_inlay",
        onlay: "workorder_surgery_onlay",
        veneer: "workorder_surgery_veneer",
      };
      const key = typeMap[treatmentType?.toLowerCase()];
      return key ? this.$t(key) : treatmentType || "";
    },

    getManufacturerText() {
      if (
        this.prostheticData?.toothDetails &&
        this.prostheticData.toothDetails.length > 0
      ) {
        // 제조사 중복 제거 후 한 줄로 표시
        const manufacturers = this.prostheticData.toothDetails.map((detail) => {
          return detail.manufacturer || "";
        });
        const uniqueManufacturers = [...new Set(manufacturers)].filter(
          (m) => m
        );

        // zh locale인 경우 덴탈맥스를 DENTALMAX로 변환
        const localizedManufacturers = uniqueManufacturers.map((name) => {
          if (
            this.$i18n.locale === "zh" &&
            (name === "덴탈맥스" || name === "DENTALMAX")
          ) {
            return "DENTALMAX";
          }
          return name;
        });

        const defaultName =
          this.$i18n.locale === "zh" ? "DENTALMAX" : "덴탈맥스";
        return localizedManufacturers.join(", ") || defaultName;
      }
      return "";
    },

    getDetailSelectionText() {
      if (
        this.prostheticData?.toothDetails &&
        this.prostheticData.toothDetails.length > 0
      ) {
        // 상세 선택 중복 제거 후 한 줄로 표시
        const detailSelections = this.prostheticData.toothDetails.map(
          (detail) => {
            const selection = detail.detailSelection || "ZIRCONIA";
            return selection === "ZIRCONIA"
              ? this.$t("workorder_label_zirconia")
              : selection;
          }
        );
        const uniqueSelections = [...new Set(detailSelections)].filter(
          (s) => s
        );
        return (
          uniqueSelections.join(", ") || this.$t("workorder_label_zirconia")
        );
      }
      return this.$t("workorder_label_zirconia");
    },

    getGtinCodeText() {
      if (
        this.prostheticData?.toothDetails &&
        this.prostheticData.toothDetails.length > 0
      ) {
        // GTIN 코드 중복 제거 후 한 줄로 표시
        const gtinCodes = this.prostheticData.toothDetails
          .map((detail) => detail.gtinCode || "")
          .filter((code) => code);
        const uniqueCodes = [...new Set(gtinCodes)];
        return uniqueCodes.join(", ");
      }
      return "";
    },

    getManufacturingDateText() {
      if (
        this.prostheticData?.toothDetails &&
        this.prostheticData.toothDetails.length > 0
      ) {
        // 제조일자 중복 제거 후 한 줄로 표시
        const dates = this.prostheticData.toothDetails
          .map((detail) => detail.manufacturingDate || "")
          .filter((date) => date);
        const uniqueDates = [...new Set(dates)];
        // YYMMDD 형식을 YYYY/MM/DD 형식으로 변환
        const formattedDates = uniqueDates.map((date) => {
          if (date.length === 6) {
            const year = "20" + date.substring(0, 2);
            const month = date.substring(2, 4);
            const day = date.substring(4, 6);
            return `${year}/${month}/${day}`;
          }
          return date;
        });
        return formattedDates.join(", ");
      }
      return "";
    },

    getLotNumberText() {
      if (
        this.prostheticData?.toothDetails &&
        this.prostheticData.toothDetails.length > 0
      ) {
        // LOT 번호 중복 제거 후 한 줄로 표시
        const lotNumbers = this.prostheticData.toothDetails
          .map((detail) => detail.lotNumber || "")
          .filter((lot) => lot);
        const uniqueLots = [...new Set(lotNumbers)];
        return uniqueLots.join(", ");
      }
      return "";
    },

    getProductText() {
      if (
        this.prostheticData?.toothDetails &&
        this.prostheticData.toothDetails.length > 0
      ) {
        // 제품명 중복 제거 후 한 줄로 표시
        const products = this.prostheticData.toothDetails.map((detail) => {
          return detail.product || "";
        });
        const uniqueProducts = [...new Set(products)].filter((p) => p);
        return uniqueProducts.join(", ") || "LUXEN Zirconia";
      }
      return "";
    },

    getSurfaceProcessText() {
      // Step3에서는 toothDetails에 stm 정보가 있음
      if (
        this.prostheticData?.toothDetails &&
        this.prostheticData.toothDetails.length > 0
      ) {
        const stmTeeth = this.prostheticData.toothDetails.filter(
          (tooth) => tooth.stm === true
        );

        if (stmTeeth.length > 0) {
          return this.$t("workorder_stm_format", { count: stmTeeth.length });
        }
      }
      return this.$t("workorder_label_none");
    },

    getHospitalName() {
      if (this.clientData?.name) {
        const hospitalName = this.clientData.name;
        return `${hospitalName}`;
      }
      return "병원명 (000-00-00000)";
    },

    getHospitalPhone() {
      return this.clientData?.call ? `(${this.clientData.call})` : "";
    },

    formatBusinessNumber(value) {
      if (!value) return "";
      const cleaned = value.replace(/\D/g, "");
      if (cleaned.length === 10) {
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(
          5
        )}`;
      }
      return value;
    },

    // 부모 컴포넌트에서 호출할 검증 메서드
    async validateForm() {
      return true; // Step4는 확인 단계이므로 항상 true
    },

    // 부모 컴포넌트에서 호출할 데이터 가져오기 메서드
    getFormData() {
      return {
        confirmed: true,
        timestamp: new Date().toISOString(),
      };
    },
  },
};
</script>

<style scoped lang="scss">
$Gray-5: #f7f8f8;
$Gray-10: #f1f2f3;
$Gray-20: #e8ebed;
$Gray-30: #dde0e4;
$Gray-50: #acb2b9;
$Gray-60: #8d939a;
$Gray-70: #6e747c;
$Gray-80: #4b5158;
$Gray-90: #2f3337;
$Gray-100: #17191c;
$Blue: #278aec;
$Yellow: #ffe414;

.step4-confirm {
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;

  .confirm-modal {
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    overflow: hidden;

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0px 14px;
      border-bottom: 1px solid #0000001a !important;
      background: white;
      height: 52px;

      h3 {
        overflow: hidden;
        color: var(--Gray-90, #2f3337);
        text-overflow: ellipsis;
        font-family: "SUIT Variable";
        font-size: 17px;
        font-style: normal;
        font-weight: 700;
        line-height: 100%; /* 17px */
        margin: 0;
      }

      .close-btn {
        width: 32px;
        height: 32px;
        border: none;
        background: transparent;
        color: $Gray-60;
        cursor: pointer;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          background: $Gray-10;
          color: $Gray-90;
        }

        i {
          font-size: 16px;
        }
      }
    }

    .modal-body {
      padding: 24px;

      .info-section {
        .info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;

          &:last-child {
            border-bottom: none;
          }

          label {
            font-family: "SUIT Variable";
            font-size: 15px;
            font-style: normal;
            font-weight: 500;
            line-height: 24px; /* 160% */
            flex: 0 0 80px;
            width: 130px;
            margin-right: 10px;
            margin-bottom: 0;
          }

          span {
            color: var(--Gray-90, #2f3337);
            font-family: "SUIT Variable";
            font-size: 15px;
            font-style: normal;
            font-weight: 400;
            line-height: 24px; /* 160% */
            text-align: left; // 왼쪽 정렬로 변경
            flex: 1;

            &.sub-text {
              color: var(--Gray-60, #8d939a);
              font-family: "SUIT Variable";
              font-size: 15px;
              font-style: normal;
              font-weight: 400;
              line-height: 24px;
            }
          }
        }
      }
    }

    .modal-footer {
      display: flex;
      justify-content: flex-end; // 오른쪽 정렬
      gap: 10px;
      padding: 12px 20px;
      border-top: 1px solid rgba(0, 0, 0, 0.1) !important;
      flex: none;
      .cancel-btn {
        padding: 0 20px;
        overflow: hidden;
        color: var(--Gray-70, #6e747c);
        text-align: center;
        text-overflow: ellipsis;
        font-family: "SUIT Variable";
        font-size: 15px;
        font-style: normal;
        font-weight: 600;
        line-height: 100%; /* 15px */
        border-radius: 8px;
        background: var(--Gray-20, #e8ebed);

        &:hover {
          border-color: $Gray-50;
          color: $Gray-90;
        }
      }

      .confirm-btn {
        width: 120px;
        flex: none;
        height: 40px;
        overflow: hidden;
        color: var(--Gray-100, #17191c);
        text-align: center;
        text-overflow: ellipsis;
        font-family: "SUIT Variable";
        font-size: 15px;
        font-style: normal;
        font-weight: 600;
        line-height: 100%; /* 15px */
        border-radius: 8px;
        background: var(--Yellow, #ffe414);

        &:hover {
          background: #ffc700;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .step4-confirm {
    padding: 16px;

    .confirm-modal {
      .modal-header,
      .modal-body,
      .modal-footer {
        padding: 16px;
      }

      .modal-body {
        .info-section {
          .info-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;

            label {
              flex: none;
            }

            span {
              text-align: left;
            }
          }
        }
      }

      .modal-footer {
        flex-direction: column;

        .cancel-btn,
        .confirm-btn {
          width: 100%;
        }
      }
    }
  }
}
</style>
